import { Injectable } from '@angular/core';
import { Observable, filter, map, tap } from 'rxjs';
import { HttpEvent, HttpEventType, HttpParams, HttpResponse } from '@angular/common/http';
import { ServiceClientService } from '../service-client.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HosAddendumService {
  constructor(private readonly serviceClientService: ServiceClientService) {}

  /**
   * Uploads readymade HOS Addendum template PDF
   *
   * With onProgress, it is told how much of the file has gone up (0-100), as the E-Sign upload shows it. The
   * reply is the same either way.
   */
  uploadTemplate(formData: FormData, agencyId?: string, onProgress?: (percent: number) => void): Observable<any> {
    const headers: Record<string, string> = {};
    if (agencyId) {
      headers['agencyid'] = agencyId;
    }
    if (!onProgress) {
      return this.serviceClientService.postImage(
        environment.endPoints.uploadHosAddendum,
        formData,
        headers,
      );
    }
    return this.serviceClientService
      .postImageWithProgress<any>(environment.endPoints.uploadHosAddendum, formData, headers)
      .pipe(
        tap((event: HttpEvent<any>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            onProgress(Math.round((100 * event.loaded) / event.total));
          }
        }),
        filter((event: HttpEvent<any>): event is HttpResponse<any> => event.type === HttpEventType.Response),
        map((event) => event.body),
      );
  }

  /**
   * Sends HOS Addendum to recipient
   */
  sendHosAddendum(payload: any, agencyId?: string): Observable<any> {
    return this.serviceClientService.post(
      environment.endPoints.sendHosAddendum,
      payload,
      'agencyid',
      agencyId,
    );
  }

  /**
   * Retracts an addendum that is out for signature. It goes back to Not Sent and its signing link
   * stops working; the document stays on the patient so it can be corrected and sent again.
   */
  retractHosAddendum(payload: { hosAddendumId: string; reason: string }, agencyId?: string): Observable<any> {
    return this.serviceClientService.post(
      environment.endPoints.retractHosAddendum,
      payload,
      'agencyid',
      agencyId,
    );
  }

  /**
   * Fetches HOS Addendum history and active record for given eSoc record
   */
  getHosAddendumHistory(esocId: string): Observable<any> {
    return this.serviceClientService.get(
      `${environment.endPoints.getHosAddendumHistory}/${esocId}`,
    );
  }

  /**
   * The live signing link of an addendum out for signature. The history no longer carries the link's code,
   * so Copy Signing Link asks for it; the server refuses when the company hides copying.
   */
  getHosAddendumSigningLink(addendumId: string): Observable<any> {
    return this.serviceClientService.get(
      `${environment.endPoints.getHosAddendumSigningLink}/${addendumId}`,
    );
  }

  /**
   * Records a start of care for this patient's HOS Addendum clock, for a patient whose record has none.
   * It is held for the addendum alone and never written to the SOC date the ioDoc lists sort on.
   */
  setHosAddendumSocDate(esocId: string, socDate: string | null, agencyId?: string): Observable<any> {
    return this.serviceClientService.post(
      environment.endPoints.setHosAddendumSocDate,
      { esocId, socDate },
      'agencyid',
      agencyId,
    );
  }

  /**
   * Checks if HOS Addendum feature is enabled for company/agency
   */
  checkHosAddendumEnabled(agencyId?: string): Observable<any> {
    let params = new HttpParams();
    if (agencyId) {
      params = params.set('agencyId', agencyId);
    }
    return this.serviceClientService.get(
      environment.endPoints.checkHosAddendumEnabled,
      params,
    );
  }

  /**
   * Lists automated delivery destinations for an agency
   */
  listDeliveryDestinations(agencyId: string): Observable<any> {
    return this.serviceClientService.get(
      `${environment.endPoints.hosAddendumDeliveryDestinationList}/${agencyId}`,
      undefined,
      'agencyid',
      agencyId,
    );
  }

  /**
   * Adds a new unverified delivery destination
   */
  createDeliveryDestination(agencyId: string, payload: { channel: string; value: string }): Observable<any> {
    return this.serviceClientService.post(
      `${environment.endPoints.hosAddendumDeliveryDestinations}/${agencyId}`,
      payload,
      'agencyid',
      agencyId,
    );
  }

  /**
   * Removes a delivery destination
   */
  deleteDeliveryDestination(agencyId: string, destinationId: string): Observable<any> {
    return this.serviceClientService.delete(
      `${environment.endPoints.hosAddendumDeliveryDestinations}/${agencyId}/${destinationId}`,
      'agencyid',
      agencyId,
    );
  }

  /**
   * Dispatches a 6-digit verification code PDF to the destination
   */
  requestDeliveryVerificationCode(agencyId: string, destinationId: string): Observable<any> {
    return this.serviceClientService.post(
      `${environment.endPoints.hosAddendumDeliveryDestinations}/${agencyId}/${destinationId}/request-code`,
      {},
      'agencyid',
      agencyId,
    );
  }

  /**
   * Verifies the 6-digit code with brute-force lockout protection
   */
  verifyDeliveryCode(agencyId: string, destinationId: string, code: string): Observable<any> {
    return this.serviceClientService.post(
      `${environment.endPoints.hosAddendumDeliveryDestinations}/${agencyId}/${destinationId}/verify`,
      { code },
      'agencyid',
      agencyId,
    );
  }

  /**
   * Toggles automated delivery enabled/disabled for a verified destination
   */
  toggleDeliveryDestination(agencyId: string, destinationId: string, enabled: boolean): Observable<any> {
    return this.serviceClientService.patch(
      `${environment.endPoints.hosAddendumDeliveryDestinations}/${agencyId}/${destinationId}/toggle`,
      { enabled },
      'agencyid',
      agencyId,
    );
  }
}
